import {
  AreaTooltip,
  ChartDataField,
  Disclaimer,
  Div,
  formatAmount,
  formatDateByGroupByTypeChart,
  GroupByType,
  lightTheme,
  NumberType,
  P,
} from '@vgrubs/components';

/**
 * Shared axis-tick renderers and chart-field factory used by the Finances
 * dashboard charts (`ReusableChart` / area / line). Extracted so each chart
 * file doesn't carry its own copy.
 */

export const CustomXAxisTick = ({
  x,
  y,
  payload,
  groupByType,
}: {
  x: number;
  y: number;
  payload: { value: string };
  groupByType: GroupByType;
}) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={0}
      y={0}
      dy={14}
      textAnchor="middle"
      fontSize="12px"
      fill={lightTheme.lightGray}
    >
      {formatDateByGroupByTypeChart(payload.value, groupByType)}
    </text>
  </g>
);

export const CurrencyYAxisTick = ({ x, y, payload }: any) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={-5}
      y={-13}
      dy={16}
      fontSize="14px"
      textAnchor="end"
      fill={lightTheme.lightGray}
    >
      {formatAmount(payload.value, NumberType.USD, true)}
    </text>
  </g>
);

/**
 * Width the Y axis needs to hold the widest label `CurrencyYAxisTick` will
 * draw for `values` (cents).
 *
 * Recharts' YAxis defaults to 60px and the tick right-aligns its text against
 * the axis' inner edge, so anything wider is painted to the LEFT of the axis -
 * off the card entirely on a narrow mobile card, where "-$21.91K" used to sit
 * outside the white rounded box. Ticks are always inside the domain, so the
 * two extremes give the longest label.
 */
export const currencyYAxisWidth = (values: number[]) => {
  const DEFAULT_WIDTH = 60;
  if (!values.length) return DEFAULT_WIDTH;
  const longest = [Math.min(...values), Math.max(...values)].reduce(
    (max, v) => Math.max(max, formatAmount(v, NumberType.USD, true).length),
    0
  );
  // 14px tick font is roughly 8px per glyph, plus the tick's own 5px offset.
  return Math.min(96, Math.max(DEFAULT_WIDTH, longest * 8 + 10));
};

/**
 * Small helper that fills the boilerplate defaults of a chart series field
 * (`strokeWidth`, opacities). Pass overrides via `options`.
 */
export const createField = (
  dataKey: string,
  color: string,
  options: Partial<ChartDataField> = {}
): ChartDataField => ({
  dataKey,
  color,
  strokeWidth: 2,
  fillOpacity: 1,
  strokeOpacity: 1,
  ...options,
});

// ─── Tooltip factory for single-metric compare-aware charts ──────────────────
//
// Default ChartTooltip only renders payload[0], so in compare mode it hides
// the past-period value. This builds a tooltip that shows both the current
// and (when present) the compared bucket value, using the chart's currency.
//
// Pass the bare dataKey that the chart uses (e.g. "totalIncome"). The factory
// looks up `<dataKey>` for the current value and `<dataKey>ToCompare` for
// the past one — matching the field naming used in FinancesCharts.

export const buildSingleMetricCompareTooltip = ({
  dataKey,
  label,
  color,
  negateForDisplay = false,
}: {
  dataKey: string;
  label: string;
  color: string;
  /**
   * Show the value with a flipped sign (e.g. losses drawn as positive bars
   * but displayed as "-$X.XX" to match accounting convention).
   */
  negateForDisplay?: boolean;
}) =>
  // eslint-disable-next-line react/display-name
  ({
    payload,
    label: xLabel,
    groupByType,
    currency,
  }: {
    payload?: { dataKey: string; value: number }[];
    label?: string;
    groupByType: GroupByType;
    currency: NumberType;
  }) => {
    if (!payload?.length) return null;
    const valueFor = (k: string) =>
      payload.find((p) => p.dataKey === k)?.value;
    const flip = (n: number | undefined) =>
      n === undefined ? undefined : negateForDisplay ? -n : n;
    const current = flip(valueFor(dataKey));
    const compare = flip(valueFor(`${dataKey}ToCompare`));

    return (
      <AreaTooltip>
        <P $m="0" $bold>
          {formatDateByGroupByTypeChart(xLabel ?? '', groupByType, true)}
        </P>
        {current !== undefined && (
          <Div $flex $alignCenter $between $gap="12px">
            <P $m="0">{label}</P>
            <P $m="0" $color={color} $bold>
              {formatAmount(current, currency)}
            </P>
          </Div>
        )}
        {compare !== undefined && (
          <Div $flex $alignCenter $between $gap="12px">
            <Disclaimer $m="0">Previous</Disclaimer>
            <Disclaimer $m="0" $color={lightTheme.darkGray} $bold>
              {formatAmount(compare, currency)}
            </Disclaimer>
          </Div>
        )}
      </AreaTooltip>
    );
  };
